import { Skeleton } from 'antd'
import classNames from 'classnames'
import { useAppSelector } from 'common/hooks/redux'
import {
  useFetchEncounterLabsQuery,
  useFetchPhotoStreamQuery,
} from 'features/Procedure/state/procedure.api'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import styles from './preview.module.scss'
import { PdfViewerErrorBoundary } from './pdf-viewer-error-boundary'
import { SyncfusionPdfViewer } from './syncfusion-viewer'

type Props = {
  documentId?: number
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  scrollMode?: 'self' | 'parent'
  viewerMode?: 'custom' | 'native'
  className?: string
}

const failedFallback = (
  <div className={styles.container}>
    <div className={styles.emptyState}>Failed to load document</div>
  </div>
)

export const LabsPreview = ({
  documentId,
  selectedPatientId,
  selectedEncounterUid,
  scrollMode = 'self',
  viewerMode = 'custom',
  className,
}: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const selectedLabId = useAppSelector((state) => state.procedureReducer.selectedLabId)

  const currentDocumentId = documentId || selectedLabId
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)

  const shouldSkipLabsQuery = !siteId || !selectedPatientId || !selectedEncounterUid

  const { isFetching: isFetchingLabs, isLoading: isLoadingLabs } = useFetchEncounterLabsQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      encounterUid: selectedEncounterUid!,
    },
    {
      skip: shouldSkipLabsQuery,
    },
  )

  const { data: pdfBlob, isFetching: isFetchingPdf } = useFetchPhotoStreamQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      documentId: currentDocumentId!,
    },
    {
      skip: !currentDocumentId || !siteId || !selectedPatientId,
    },
  )

  useEffect(() => {
    if (!pdfBlob || !(pdfBlob instanceof Blob)) {
      setPdfUrl(null)
      return
    }

    const url = URL.createObjectURL(pdfBlob)
    setPdfUrl(url)

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [pdfBlob])

  useEffect(() => {
    if (!currentDocumentId) {
      setPdfUrl(null)
    }
  }, [currentDocumentId])

  const renderContent = () => {
    // Show skeleton when labs are fetching or PDF is fetching
    if (isFetchingLabs || isLoadingLabs || isFetchingPdf) {
      return (
        <div className={styles.container}>
          <Skeleton active paragraph={{ rows: 12 }} />
        </div>
      )
    }

    // Show empty state only when no document is selected
    if (!currentDocumentId) {
      return (
        <div className={styles.container}>
          <div className={styles.emptyState}>
            <span>No document selected</span>
          </div>
        </div>
      )
    }

    if (!pdfUrl) {
      return failedFallback
    }

    if (viewerMode === 'native') {
      const nativeViewerUrl = `${pdfUrl}#zoom=80&navpanes=0`
      return (
        <div className={styles.container}>
          <iframe title='Document preview' src={nativeViewerUrl} className={styles.nativeViewer} />
        </div>
      )
    }

    return (
      <div className={classNames(styles.container, styles.containerFullHeight, className)}>
        <SyncfusionPdfViewer
          pdfUrl={pdfUrl}
          documentId={currentDocumentId}
          scrollMode={scrollMode}
        />
      </div>
    )
  }

  // The boundary stays mounted across skeleton/viewer swaps, so viewer teardown
  // errors (e.g. switching encounter with a PDF open) are contained here.
  return (
    <PdfViewerErrorBoundary resetKey={currentDocumentId ?? null} fallback={failedFallback}>
      {renderContent()}
    </PdfViewerErrorBoundary>
  )
}
